import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'styles/util';
import { clearStorageSites } from 'actions/account/storageSitesSection';
import { fetchStorageSite, fetchBookingsPriceAffectedByStorageSiteUpdate, updateStorageSite } from 'actions/storageSites';
import strings from 'localization/strings';
import { createFormDataForStorageSite, createStorageSiteRequestFromFormData } from 'logic/editStorageSiteLogic';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { handleResponse } from 'actions/actionHelpers';
import { getResizedImageUrl } from 'helpers/ImageHelper';
import storageSiteStatuses from 'enums/storageSiteStatuses';
import contactTypes from 'enums/contactTypes';
import storageSiteBookingUpdateModes from 'enums/storageSiteBookingUpdateModes';
import { googleTagManagerConstants, createGoogleTagManagerUserAction } from 'integration/google-tag-manager/googleTagManagerHelper';
import { getStorageSiteDetailsUrl } from 'helpers/StorageSiteHelper';
import queryString from 'query-string';
import { startOfYear, endOfYear } from 'date-fns';
import { formatIsoDate } from 'helpers/DateHelper';
import routes from 'routes';
import clsx from 'clsx';

import StorageSiteForm from './StorageSiteForm';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Loader from 'common/Loader';
import AccountLink from 'common/AccountLink';
import ContentBox from 'common/ContentBox';
import EditStep0 from './EditStep0';
import EditStep1 from './EditStep1';
import EditStep2 from './EditStep2';
import EditStep3 from './EditStep3';
import EditStep3b from './EditStep3b';
import EditStep4 from './EditStep4';
import EditStep5 from './EditStep5';
import DisplayStep0 from './DisplayStep0';
import DisplayStep1 from './DisplayStep1';
import DisplayStep2 from './DisplayStep2';
import DisplayStep3 from './DisplayStep3';
import DisplayStep3b from './DisplayStep3b';
import DisplayStep4 from './DisplayStep4';
import DisplayStep5 from './DisplayStep5';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import DraftSubmittedDialog from './DraftSubmittedDialog';
import PriceAffectedBookingsDialog from './PriceAffectedBookingsDialog';
import Alert from '@material-ui/lab/Alert';

const EditStorageSite = () => {
    const useStyles = makeStyles(({ theme, colors }) => ({
        editContainer: {
            position: 'relative'
        },
        show: {
            display: 'block'
        },
        hide: {
            display: 'none'
        },
        displayHeader: {
            display: 'flex',
            marginBottom: theme.spacing(2)
        },
        displayTitle: {
            flex: '1 1 auto'
        },
        editButton: {
            flex: '0 0 auto'
        },
        editStepButtonContainer: {
            marginTop: theme.spacing(2),
            textAlign: 'right',
            '& > *': {
                marginLeft: theme.spacing(2)
            }
        },
        editStepFullWidthButtonContainer: {
            marginTop: theme.spacing(2),
            '& > *': {
                marginBottom: theme.spacing(1)
            },
            '& > *:last-child': {
                marginBottom: 0
            }
        },
        disabledContainer: {
            border: 'none',
            margin: 0,
            padding: 0,
            '&:disabled .editContainer': {
                opacity: 0.333
            }
        },
        saveIndicator: {
            position: 'absolute',
            left: '50%',
            top: '50%',
            margin: '-50px'
        },
        mainImage: {
            width: '100%'
        },
        accountActions: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(1.875),
            '& > *': {
                flex: `1 1 calc(50% - ${theme.spacing(1.875)}px)`,
                [theme.breakpoints.down('xs')]: {
                    flex: '1 1 100%'
                }
            }
        },
        titleBoxButtons: {
            display: 'flex',
            gap: theme.spacing(1.875),
            flexWrap: 'wrap'
        },
        steps: {
            '& > *': {
                padding: theme.spacing(4, 0),
                borderBottom: `2px solid ${colors.mediumGrey}`,
                '&:first-child': {
                    paddingTop: 0
                },
                '&:last-child': {
                    paddingBottom: 0,
                    borderBottom: 'none'
                }
            }
        }
    }));
    const classes = useStyles();

    const { appContext } = useAppContext();
    const params = useParams();
    const dispatch = useDispatch();
    const history = useHistory();

    const [isSaving, setIsSaving] = useState(false);
    const [storageSite, setStorageSite] = useState(undefined);
    const [storageSiteFormData, setStorageSiteFormData] = useState(undefined);
    const [storageSiteFormDataBeforeEdit, setStorageSiteFormDataBeforeEdit] = useState(undefined);
    const [stepBeingEdited, setStepBeingEdited] = useState(undefined); // zero-based
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [draftSubmittedDialogOpen, setDraftSubmittedDialogOpen] = useState(false);
    const [saveCallback, setSaveCallback] = useState(undefined);
    const [priceAffectedBookings, setPriceAffectedBookings] = useState(undefined);
    const [priceAffectedBookingsDialogOpen, setPriceAffectedBookingsDialogOpen] = useState(false);
    const authenticationContext = useSelector(state => state.authentication.context);
    const selectedActor = authenticationContext.selectedActor;

    const buttonFullWidth = !useMediaQuery(theme => theme.breakpoints.up('md'));

    const storageSiteId = parseInt(params.storageSiteId, 10);

    useEffect(() => {
        const startDate = authenticationContext.isSuperAdminOrImpersonated
            ? formatIsoDate(startOfYear(new Date()), appContext)
            : undefined;
        const endDate = authenticationContext.isSuperAdminOrImpersonated
            ? formatIsoDate(endOfYear(new Date()), appContext)
            : undefined;
        dispatch(fetchStorageSite(storageSiteId, startDate, endDate, false /* includeStorageGroupBookingCalendars */, true /* includeOpeningTimeSpansConfiguration */, true /* includeTimePeriodPriceEditabilities */))
            .then(handleResponse(
                response => {
                    const formData = createFormDataForStorageSite(response.payload, appContext);
                    setStorageSite(response.payload);
                    setStorageSiteFormData(formData);
                    setStorageSiteFormDataBeforeEdit(formData);
                }
            ));
    }, []);

    const storageSiteBookingOverviewUrl = routes.account.storageSiteBookingOverview.replace(':storageSiteId', params.storageSiteId);

    const showLockCodeRequestTableUrl = storageSite?.storageGroups.find(storageGroup => storageGroup.lockConfigurations.length > 0)
        ? routes.account.storageSiteLockCodeRequests.replace(':storageSiteId', params.storageSiteId)
        : undefined;

    const roundReportsEnabled = storageSite?.contacts.some(o => o.type === contactTypes.roundReportEmail.key);

    const handleFormSubmit = () => {
        // we are saving at each step editing, so nothing to do here
    };

    const validate = values => {
        if (values.steps[4].images.length === 0) {
            return { '': strings.atLeastOneImageIsRequired };
        }
        return undefined;
    };

    const handleEditClick = (form, step) => {
        if(stepBeingEdited !== undefined) {
            revertToFormValuesBeforeEditing(form);
        }
        setStepBeingEdited(step);
    };

    const handleSaveClick = (form, considerPriceAffectedBookings, callback) => {
        if(!considerPriceAffectedBookings) {
            save(form, false, callback);
            return;
        }

        setIsSaving(true);
        setSaveCallback(callback);
        const formValues = form.getState().values;

        const baseValues = {
            ownerType: selectedActor.organizationType
        };
        const updateStorageSiteRequest = createStorageSiteRequestFromFormData(formValues, baseValues, selectedActor, undefined, appContext);
        // all active subscription bookings should be checked
        updateStorageSiteRequest.bookingUpdateSettings = {
            updateMode: storageSiteBookingUpdateModes.allActiveSubscriptionBookings.key
        };

        dispatch(fetchBookingsPriceAffectedByStorageSiteUpdate(storageSiteId, updateStorageSiteRequest))
            .then(handleResponse(
                response => {
                    if(response.payload.length === 0) {
                        save(form, false, saveCallback);
                    } else {
                        setPriceAffectedBookings(response.payload);
                        form.mutators.changeValue({ name: 'priceAffectedBookingsDialog.bookings', value: response.payload.map(o => ({ id: o.existingBooking.id, selected: true }))});
                        setPriceAffectedBookingsDialogOpen(true);
                        setIsSaving(false);
                    }
                },
                () => setIsSaving(false)
            ));
    };

    const save = (form, considerPriceAffectedBookings, callback) => {
        setIsSaving(true);
        const formValues = form.getState().values;
        setStorageSiteFormDataBeforeEdit({...formValues});

        const baseValues = {
            ownerType: selectedActor.organizationType
        };
        const updateStorageSiteRequest = createStorageSiteRequestFromFormData(formValues, baseValues, selectedActor, considerPriceAffectedBookings ? priceAffectedBookings : undefined, appContext);

        dispatch(updateStorageSite(storageSiteId, updateStorageSiteRequest))
            .then(handleResponse(
                response => {
                    dispatch(createGoogleTagManagerUserAction(googleTagManagerConstants.userActions.storageSiteUpdated));
                    setIsSaving(false);
                    dispatch(clearStorageSites());
                    setStepBeingEdited(undefined);
                    const formData = createFormDataForStorageSite(response.payload, appContext);
                    setStorageSite(response.payload);
                    setStorageSiteFormData(formData);
                    setStorageSiteFormDataBeforeEdit(formData);
                    if(callback) {
                        callback();
                    }
                    return { snackbar: { message: strings.storageSiteUpdated } };
                },
                () => setIsSaving(false)
            ));
    };

    const revertToFormValuesBeforeEditing = form => {
        form.mutators.changeValue({ name: '', value: { ...storageSiteFormDataBeforeEdit } });
    };

    const handleSaveStorageGroup = form => {
        handleSaveClick(form, true, () => setStepBeingEdited(0));
    };

    const handleCancelEditClick = form => {
        revertToFormValuesBeforeEditing(form);
        setStepBeingEdited(undefined);
    };

    const handleConfirmDeleteDialogOkClick = form => {
        setConfirmDeleteDialogOpen(false);
        form.mutators.changeValue({ name: 'steps[0].status', value: storageSiteStatuses.deleted.key });
        handleSaveClick(form, false, () => history.push(routes.account.storageSites));
    };

    const handleConfirmDeleteDialogCancelClick = () => {
        setConfirmDeleteDialogOpen(false);
    };

    const handleDraftSubmittedDialogCloseClick = () => {
        setDraftSubmittedDialogOpen(false);
    };

    const handlePriceAffectedBookingsDialogSaveClick = form => {
        setPriceAffectedBookingsDialogOpen(false);
        save(form, true, saveCallback);
    };

    const handlePriceAffectedBookingsDialogCancelClick = form => {
        revertToFormValuesBeforeEditing(form);
        setPriceAffectedBookingsDialogOpen(false);
    };

    const handleDeleteClick = () => {
        setConfirmDeleteDialogOpen(true);
    };

    const handleCancelClick = () => {
        history.push(routes.account.storageSites);
    };

    const handleApproveClick = form => {
        form.mutators.changeValue({ name: 'steps[0].status', value: storageSiteStatuses.approved.key });
        handleSaveClick(form, false);
    };

    const handleSubmitForApprovalClick = form => {
        form.mutators.changeValue({ name: 'steps[0].status', value: storageSiteStatuses.submittedDraft.key });
        handleSaveClick(form, false, () => {
            setDraftSubmittedDialogOpen(true);
        });
    };

    const handleToggleActivateClick = form => {
        const formValues = form.getState().values;
        const newStatus = formValues.steps[0].status === storageSiteStatuses.approved.key
            ? storageSiteStatuses.hidden.key
            : storageSiteStatuses.approved.key;
        form.mutators.changeValue({ name: 'steps[0].status', value: newStatus });
        handleSaveClick(form, false);
    };

    const SavingOverlay = () => <Box className={classes.saveIndicator}><Loader left={0} top={0}/></Box>;

    const TitleBoxContainer = ({ form }) => {
        const formValues = form.getState().values;
        const mainImage = formValues.steps[4].images[0];
        const status = formValues.steps[0].status;

        return (
            <Box>
                <Box className={clsx('editContainer', classes.editContainer)}>
                    { isSaving && <SavingOverlay/> }
                    <ContentBox widgetSpacing="30px" className={isSaving ? classes.isSaving : ''}>
                        {
                            mainImage &&
                            (
                                <img className={classes.mainImage} src={getResizedImageUrl(mainImage.mediaUrl, { width: 650, height: 407, bgcolor: 'ffffff' })} />
                            )
                        }

                        <Typography variant="h2" display="block">
                            {formValues.steps[4].title}
                        </Typography>

                        <Box className={classes.accountActions}>
                            {
                                storageSite &&
                                (
                                    <>
                                        <AccountLink to={getStorageSiteDetailsUrl(storageSite)}>
                                            {strings.showStorageSite}
                                        </AccountLink>
                                        <AccountLink to={storageSiteBookingOverviewUrl}>
                                            {strings.showBookingOverview}
                                        </AccountLink>
                                    </>
                                )
                            }
                            <AccountLink to={routes.account.ownerBookings + '?' + queryString.stringify({ storageSiteId: storageSiteId })}>
                                {strings.showBookings}
                            </AccountLink>
                            {
                                showLockCodeRequestTableUrl && <AccountLink to={showLockCodeRequestTableUrl}>{strings.showStorageSiteLockCodeRequests}</AccountLink>
                            }
                            {
                                roundReportsEnabled && <AccountLink to={routes.account.storageSiteRoundReports.replace(':storageSiteId', storageSiteId)}>{strings.showRoundReports}</AccountLink>
                            }
                        </Box>

                        <Box className={classes.titleBoxButtons}>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={isSaving}
                                fullWidth={buttonFullWidth}
                                onClick={handleDeleteClick}
                            >
                                {strings.remove}
                            </Button>
                            {
                                status === storageSiteStatuses.submittedDraft.key && authenticationContext.isSuperAdminOrImpersonated &&
                                (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={isSaving}
                                        fullWidth={buttonFullWidth}
                                        onClick={() => handleApproveClick(form)}
                                    >
                                        {strings.approve}
                                    </Button>
                                )
                            }
                            {
                                status === storageSiteStatuses.unsubmittedDraft.key &&
                                (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={isSaving}
                                        fullWidth={buttonFullWidth}
                                        onClick={() => handleSubmitForApprovalClick(form)}
                                    >
                                        {strings.publishStorageSite}
                                    </Button>
                                )
                            }
                            {
                                (status === storageSiteStatuses.approved.key || status === storageSiteStatuses.hidden.key) &&
                                (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={isSaving}
                                        fullWidth={buttonFullWidth}
                                        onClick={() => handleToggleActivateClick(form)}
                                    >
                                        {status === storageSiteStatuses.approved.key ? strings.inactivate : strings.activate}
                                    </Button>
                                )
                            }
                            <Button
                                variant="contained"
                                color="secondary"
                                fullWidth={buttonFullWidth}
                                onClick={handleCancelClick}
                            >
                                {strings.goBack}
                            </Button>
                        </Box>
                    </ContentBox>
                </Box>
            </Box>
        );
    };

    TitleBoxContainer.propTypes = {
        form: PropTypes.object.isRequired
    };

    const StepContainer = ({ displayTitle, displayComponent, editComponent, edit, form, valid, onEditClick }) => {
        return (
            <ContentBox widgetSpacing="30px">
                <Box className={clsx('editContainer', classes.editContainer, {[classes.show]: edit, [classes.hide]: !edit})}>
                    <Box className={isSaving ? classes.isSaving : ''}>
                        {editComponent}
                    </Box>
                    { isSaving && <SavingOverlay/> }
                    <Box className={buttonFullWidth ? classes.editStepFullWidthButtonContainer : classes.editStepButtonContainer}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth={buttonFullWidth}
                            disabled={!valid || isSaving}
                            onClick={() => handleSaveClick(form, false)}
                        >
                            {strings.save}
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            fullWidth={buttonFullWidth}
                            type="button"
                            disabled={isSaving}
                            onClick={() => handleCancelEditClick(form)}
                        >
                            {strings.cancel}
                        </Button>
                    </Box>
                </Box>
                <Box className={edit ? classes.hide : classes.show}>
                    <Box className={classes.displayHeader}>
                        <Typography className={classes.displayTitle} variant="h4">{displayTitle}</Typography>
                        <Button
                            className={classes.editButton}
                            variant="contained"
                            color="primary"
                            onClick={onEditClick}
                        >
                            {strings.change}
                        </Button>
                    </Box>
                    {displayComponent}
                </Box>
            </ContentBox>
        );
    };

    StepContainer.propTypes = {
        displayTitle: PropTypes.string.isRequired,
        displayComponent: PropTypes.object.isRequired,
        editComponent: PropTypes.object.isRequired,
        edit: PropTypes.bool.isRequired,
        form: PropTypes.object.isRequired,
        valid: PropTypes.bool.isRequired,
        onEditClick: PropTypes.func.isRequired,
    };

    return (
        <Box className={classes.container}>
            {
                !storageSiteFormData && <Loader />
            }
            {
                storageSiteFormData && (
                    <StorageSiteForm
                        initialValues={storageSiteFormData}
                        onSubmit={handleFormSubmit}
                        validate={validate}
                    >
                        {
                            ({ form, valid, values, invalid, errors, handleSubmit }) => {
                                const step0Title = strings.categoryTitle;
                                return (
                                    <form onSubmit={handleSubmit}>
                                        <fieldset className={classes.disabledContainer} disabled={isSaving}>
                                            <ContentBox className={classes.steps}>
                                                <TitleBoxContainer form={form} />

                                                <StepContainer
                                                    displayTitle={step0Title}
                                                    displayComponent={<DisplayStep0 storageSiteId={storageSiteId} />}
                                                    editComponent={<EditStep0 showStepNumber={false} storageSiteExists onSaveStorageGroup={() => handleSaveStorageGroup(form)} />}
                                                    edit={stepBeingEdited === 0}
                                                    form={form}
                                                    valid={valid}
                                                    onEditClick={() => handleEditClick(form, 0)}
                                                />

                                                <StepContainer
                                                    displayTitle={strings.address}
                                                    displayComponent={<DisplayStep1/>}
                                                    editComponent={<EditStep1 showStepNumber={false} />}
                                                    edit={stepBeingEdited === 1}
                                                    form={form}
                                                    valid={valid}
                                                    onEditClick={() => handleEditClick(form, 1)}
                                                />

                                                <StepContainer
                                                    displayTitle={strings.propertiesAndFacilities}
                                                    displayComponent={<DisplayStep2/>}
                                                    editComponent={<EditStep2 showStepNumber={false} />}
                                                    edit={stepBeingEdited === 2}
                                                    form={form}
                                                    valid={valid}
                                                    onEditClick={() => handleEditClick(form, 2)}
                                                />

                                                <StepContainer
                                                    displayTitle={strings.openingTimeSpanRules}
                                                    displayComponent={<DisplayStep3/>}
                                                    editComponent={<EditStep3 showStepNumber={false} />}
                                                    edit={stepBeingEdited === 3}
                                                    form={form}
                                                    valid={valid}
                                                    onEditClick={() => handleEditClick(form, 3)}
                                                />

                                                <StepContainer
                                                    displayTitle={strings.discounts}
                                                    displayComponent={<DisplayStep3b/>}
                                                    editComponent={<EditStep3b showStepNumber={false} />}
                                                    edit={stepBeingEdited === '3b'}
                                                    form={form}
                                                    valid={valid}
                                                    onEditClick={() => handleEditClick(form, '3b')}
                                                />

                                                <StepContainer
                                                    displayTitle={strings.storageSiteTextAndImagesTitle}
                                                    displayComponent={<DisplayStep4/>}
                                                    editComponent={<EditStep4 showStepNumber={false} />}
                                                    edit={stepBeingEdited === 4}
                                                    form={form}
                                                    valid={valid}
                                                    onEditClick={() => handleEditClick(form, 4)}
                                                />

                                                {
                                                    authenticationContext.isSuperAdminOrImpersonated &&
                                                    (
                                                        <StepContainer
                                                            displayTitle={strings.storageSiteSettingsForSuperAdmin}
                                                            displayComponent={<DisplayStep5/>}
                                                            editComponent={<EditStep5 showStepNumber={false} actor={selectedActor} />}
                                                            edit={stepBeingEdited === 5}
                                                            form={form}
                                                            valid={valid}
                                                            onEditClick={() => handleEditClick(form, 5)}
                                                        />
                                                    )
                                                }
                                            </ContentBox>
                                        </fieldset>
                                        {
                                            invalid && authenticationContext.isSuperAdminOrImpersonated &&
                                            (
                                                <>
                                                    <Alert severity="error">
                                                        <Typography variant="body1" gutterBottom>
                                                            {JSON.stringify(errors)}
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {JSON.stringify(values)}
                                                        </Typography>
                                                    </Alert>
                                                </>
                                            )
                                        }

                                        <ConfirmDeleteDialog
                                            open={confirmDeleteDialogOpen}
                                            onOk={() => handleConfirmDeleteDialogOkClick(form)}
                                            onCancel={handleConfirmDeleteDialogCancelClick}
                                        />

                                        <DraftSubmittedDialog
                                            open={draftSubmittedDialogOpen}
                                            onClose={handleDraftSubmittedDialogCloseClick}
                                        />

                                        <PriceAffectedBookingsDialog
                                            priceAffectedBookings={priceAffectedBookings}
                                            open={priceAffectedBookingsDialogOpen}
                                            onSave={() => handlePriceAffectedBookingsDialogSaveClick(form)}
                                            onCancel={() => handlePriceAffectedBookingsDialogCancelClick(form)}
                                        />
                                    </form>
                                );
                            }
                        }
                    </StorageSiteForm>
                )
            }
        </Box>
    );
};

export default EditStorageSite;
